import 'font-awesome/css/font-awesome.css';
import 'foundation-icons/foundation-icons.css';
import 'sanitize.css/sanitize.css';
import '@lib/utils/polyfills';
// Import CSS reset and Global Styles
import './global-styles';

import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider } from 'react-redux';
import { RelayEnvironmentProvider } from 'react-relay/hooks';
import { applyRouterMiddleware, browserHistory, Router } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
// @ts-ignore
import { useScroll } from 'react-router-scroll';
import chmln from '@chamaeleonidae/chmln';

import { datadogRum } from '@datadog/browser-rum';
import { init as initFullstory } from '@fullstory/browser';
import { MuiThemeProvider as MuiThemeProviderNext } from '@material-ui/core/styles';
import { Buffer } from 'buffer';
import configureStore from 'configureStore';
import translationMessages from 'i18n/translationMessages';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import { ReactQuerySubscriptions } from 'requests/subscriptions';
import { createRoutes } from 'routes';
import * as serviceWorker from 'serviceWorker';
import { axiosService, getQueryConfig } from 'ui-model';

import { LanguageProvider } from '@lib/providers';
import axiosClientService from '@lib/services/axiosClientService';
import configService from '@lib/services/configService';
import { isAnyMobile } from '@lib/services/navigatorService';
import storageService from '@lib/services/storageService';
import axiosClient from '@lib/utils/axiosClient';
import recentlyUsedRequest from '@lib/utils/recentlyUsed';
import RelayEnvironment from '@lib/utils/RelayEnvironment';
import storageClient from '@lib/utils/storageClient';

import { NextyProvider } from 'containers/App/contexts';
import { makeSelectLocationState } from 'containers/App/selectors';
import { deprecatedMuiTheme, muiThemeLuma } from 'themes';

import ModulesProvider from './ModulesProvider';

(window as any).commitHash = configService.getCommitHash();
(window as any).Buffer = Buffer;

// For HubJr mobile friendly page
// https://lumahealth.clickup.com/t/864dp0p84
const isMobile = isAnyMobile();
const mainScreenMobileParam = new URLSearchParams(window.location.search).get('mainScreenMobile');
if (isMobile && mainScreenMobileParam) {
  sessionStorage.setItem('mainScreenMobile', mainScreenMobileParam);
}

// Initialize primary GTM container
const gtmId = configService.getGtmId();
if (gtmId) {
  TagManager.initialize({ gtmId });
}

// NOTE: Set up different shared-lib services
axiosClientService.set(axiosClient);
storageService.setStorage(storageClient);
axiosService.interceptors.response.use((response) => {
  const regexp = /localhost:3000|next/;
  if (window && regexp.test(window.location.href)) recentlyUsedRequest();
  return response;
});

// Create redux store with history
// this uses the singleton browserHistory provided by react-router
// Optionally, this could be changed to leverage a created history
// e.g. `const browserHistory = useRouterHistory(createBrowserHistory)();`
const store = configureStore(browserHistory);

// Sync history and store, as the react-router-redux reducer
// is under the non-default key ("routing"), selectLocationState
// must be provided for resolving how to retrieve the "route" in the state
const history = syncHistoryWithStore(browserHistory, store, {
  selectLocationState: makeSelectLocationState(),
});

// Set up the router, wrapping all Routes in the App component
const routes = createRoutes(store);

// Initialize Fullstory Logger
initFullstory({
  orgId: process.env.REACT_APP_FULLSTORY_ID || '',
  namespace: `web-app-fullstory`,
  devMode: process.env.REACT_APP_POD_NAME !== 'production',
});

// Initialize Datadog RUM
if (process.env.REACT_APP_POD_NAME === 'production') {
  datadogRum.init({
    applicationId: configService.getDatadogApplicationId() || '',
    clientToken: configService.getDatadogClientToken() || '',
    site: 'datadoghq.com',
    service: 'web-app',
    env: process.env.REACT_APP_POD_NAME,
    version: configService.getCommitHash(),
    sessionSampleRate: 20,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    startSessionReplayRecordingManually: true,
    allowedTracingUrls: [/https:\/\/.*\.lumahealth\.io/],
  });
}

// Initialize Chameleon
// chmln.init adds a global chmln object to the window
if (!window.chmln) {
  chmln.init('SmyhpiQUvy5rz2xywaHgkq0aVcK7vWNL8I1lk3O3swEXV7-1KydUG-CtPRDi3qBnbDPlj4');
}

// Initialize msw mock worker
if (process.env.NODE_ENV === 'development') {
  const { worker } = require('./mocks/browser');

  worker.start({ onUnhandledRequest: 'bypass' });
}

const queryClient = new QueryClient(getQueryConfig());

ReactDOM.render(
  <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <ReactQuerySubscriptions>
        <MuiThemeProvider muiTheme={deprecatedMuiTheme}>
          <MuiThemeProviderNext theme={muiThemeLuma}>
            <LanguageProvider messages={translationMessages} onError={() => {}}>
              <RelayEnvironmentProvider environment={RelayEnvironment}>
                <DndProvider backend={HTML5Backend}>
                  <ModulesProvider>
                    <NextyProvider>
                      <Router
                        history={history}
                        routes={routes}
                        render={
                          // Scroll to top when going to a new page, imitating default browser
                          // behavior
                          applyRouterMiddleware(useScroll()) // eslint-disable-line
                        }
                      />
                    </NextyProvider>
                  </ModulesProvider>
                </DndProvider>
              </RelayEnvironmentProvider>
            </LanguageProvider>
          </MuiThemeProviderNext>
        </MuiThemeProvider>
      </ReactQuerySubscriptions>
    </Provider>
    <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
  </QueryClientProvider>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
